import React, { useEffect, useState } from 'react';
import { Drawer, Typography, Divider, Tag, Spin, message, Empty, Space } from 'antd';
import { getPageVersionApprovalDetails } from '../../services/pageversionservice';

const { Title, Text } = Typography;

interface Props {
    approvalId: string | null;
    open: boolean;
    onClose: () => void;
}

const PageVersionApprovalDetailsDrawer: React.FC<Props> = ({
    approvalId,
    open,
    onClose
}) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<any>(null);

    useEffect(() => {
        if (open && approvalId) {
            fetchDetails();
        }
    }, [open, approvalId]);

    useEffect(() => {
        if (!open) setData(null);
    }, [open]);

    const fetchDetails = async () => {
        setLoading(true);
        try {
            const result = await getPageVersionApprovalDetails(approvalId!);
            setData(result);
        } catch {
            message.error('Failed to load approval details');
        } finally {
            setLoading(false);
        }
    };

    const safeParse = (json?: string) => {
        try {
            return json ? JSON.parse(json) : null;
        } catch {
            return null;
        }
    };

    const renderConflicts = (conflicts: any[]) => {
        return conflicts.map((c) => (
            <div key={c.StoreGuid} style={{ marginBottom: 12 }}>
                <Text strong>{c.StoreNumber} - {c.BuildingName}</Text>
                <ul>
                    {c.OverlappingPageVersions?.map((pv: any) => (
                        <li key={pv.PageVersionGuid}>
                            {pv.PageVersionName} (
                            {new Date(pv.StartDate).toLocaleDateString()} →{' '}
                            {new Date(pv.EndDate).toLocaleDateString()})
                            [{pv.ConflictType}]
                        </li>
                    ))}
                </ul>
            </div>
        ));
    };

    const renderContent = () => {
        if (!data) return <Empty />;

        const payload = safeParse(data.payload) || {};
        const failure = safeParse(data.failureReason);

        return (
            <>
                <Title level={4}>Basic Info</Title>
                <p><b>Page Version:</b> {data.pageVersionName}</p>
                <p><b>Status:</b>  <Tag color={
                    data.status === 'Approved' ? 'green' :
                        data.status === 'Rejected' ? 'red' :
                            data.status === 'Failed' ? 'red' :
                                'orange'

                }>
                    {data.status}
                </Tag></p>

                <p><b>Requested By:</b> {data.createdBy}</p>
                <p><b>Requested On:</b> {new Date(data.dateCreated).toLocaleString()}</p>

                {data.approver && (
                    <>
                        <p><b>Reviewed By:</b> {data.approver}</p>
                        <p><b>Reviewed On:</b> {new Date(data.dateModified).toLocaleString()}</p>
                    </>
                )}

                <Divider />

                <Title level={4}>Schedule</Title>
                <p><b>Start:</b> {new Date(payload.StartDate).toLocaleString()}</p>
                <p><b>End:</b> {payload.EndDate ? new Date(payload.EndDate).toLocaleString() : 'No End Date'}</p>

                <Divider />

                <Title level={4}>Stores</Title>
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Space wrap>
                        {data.selectedStores?.map((num: string) => (
                            <Tag key={num}>{num}</Tag>
                        ))}
                    </Space>
                </Space>

                <Divider />

                <Title level={4}>Override</Title>
                <Tag color={data.override ? 'red' : 'green'}>
                    {data.override ? 'Enabled' : 'No'}
                </Tag>

                {payload.Conflicts?.length > 0 && (
                    <>
                        <Divider />
                        <Title level={4}>Conflicts at Submission</Title>
                        {renderConflicts(payload.Conflicts)}
                    </>
                )}

                {failure?.length > 0 && (
                    <>
                        <Divider />
                        <Title level={4}>Failure Details</Title>
                        {renderConflicts(failure)}
                    </>
                )}
            </>
        );
    };

    return (
        <Drawer
            title="Approval Details"
            width={650}
            open={open}
            onClose={onClose}
            destroyOnClose
            styles={{
                header: {
                    color: 'white',
                    backgroundColor: 'var(--color-primary)',
                    fontWeight: 350,
                    fontSize: '14px',
                    border: 0,
                    minHeight: '40px',
                },
            }}
        >
            {loading ?
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        minHeight: '300px'
                    }}
                >
                    <Spin size="large" />
                </div>
                : renderContent()}
        </Drawer>
    );
};

export default PageVersionApprovalDetailsDrawer;